/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Confirm deleting a material, and say plainly which other classes lose it too.
 * @state-owner dataset store owns the material
 * @allowed-imports React; dataset store; shared components and hooks; activities module actions
 * @forbidden direct API client; silent destructive writes
 * @public-api class materials table
 * @side-effects deletes the activity from the library
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { ModalDocument } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import type { ActivityRecord } from '../../../shared/lib/model';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useActivityActions } from '../../activities';

export function MaterialDeleteDialog({
  activity,
  classIndex,
}: {
  activity: ActivityRecord;
  classIndex: number;
}) {
  const t = useTranslator();
  const { closeModal } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const { remove } = useActivityActions();

  /** Deleting is library-wide, so a material shared with another class disappears there too. */
  const alsoUsedBy = activity.usedByClassIndexes
    .filter((index) => index !== classIndex)
    .map((index) => classes[index]?.name)
    .filter(Boolean);

  return (
    <ModalDocument
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('取消')}
          </button>
          <button
            className="bt danger"
            type="button"
            onClick={() => {
              remove(activity.id);
              closeModal();
            }}
          >
            {t('刪除')}
          </button>
        </>
      }
      maxWidth={460}
      subtitle={activity.name}
      title={t('要刪除這份教材嗎')}
    >
      <p className="hbv">{t('刪除後這份教材會從教材庫消失，已經發出去的作業不受影響。')}</p>
      {alsoUsedBy.length ? (
        <p className="rpcav">{`${t('這份教材也在使用中')}：${alsoUsedBy.join('、')}`}</p>
      ) : null}
    </ModalDocument>
  );
}
